import {useEffect, useState} from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {stripePromise} from "../../common/stripe";
import {StripePaymentProcessing} from "../../common/components/stripe-payment-processing";

const elementAppearance = {
  theme: "night" as const,
  variables: {
    colorPrimary: "#14b8a6",
    colorBackground: "#020617",
    colorText: "#f8fafc",
    colorDanger: "#ef4444",
    fontFamily: "Inter, system-ui, sans-serif",
    borderRadius: "8px",
  },
};

// ── Inner form (must be inside <Elements>) ────────────────────────────────────

type PaymentFormInnerProps = {
  amountBrl: number;
  clientSecret: string;
  onSuccess: (intentId?: string) => void | Promise<void>;
  onCancel: () => void;
};

const PaymentFormInner = ({amountBrl, clientSecret, onSuccess, onCancel}: PaymentFormInnerProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeSuccess = async (intentId?: string) => {
    try {
      await onSuccess(intentId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pagamento confirmado, mas não foi possível atualizar a assinatura.");
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const {error: submitErr} = await elements.submit();
    if (submitErr) {
      setError(submitErr.message || "Erro ao processar pagamento.");
      setLoading(false);
      return;
    }

    const isSetupIntent = clientSecret.startsWith("seti_");

    if (isSetupIntent) {
      const {error: confirmErr, setupIntent} = await stripe.confirmSetup({
        elements,
        confirmParams: {return_url: `${window.location.origin}/configuracoes`},
        redirect: "if_required",
      });

      if (confirmErr) {
        setError(confirmErr.message || "Validação recusada. Tente novamente.");
        setLoading(false);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        await completeSuccess(setupIntent.id);
        setLoading(false);
        return;
      }
    } else {
      const {error: confirmErr, paymentIntent} = await stripe.confirmPayment({
        elements,
        confirmParams: {return_url: `${window.location.origin}/configuracoes`},
        redirect: "if_required",
      });

      if (confirmErr) {
        setError(confirmErr.message || "Pagamento recusado. Tente novamente.");
        setLoading(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        await completeSuccess(paymentIntent.id);
        setLoading(false);
        return;
      }
    }

    setError("Status de pagamento inesperado. Contate o suporte.");
    setLoading(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Box sx={{p: 2, borderRadius: 2, bgcolor: "rgba(20,184,166,0.08)", border: "1px solid rgba(20,184,166,0.2)"}}>
          <Typography sx={{color: "rgba(226,232,240,0.7)", fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5}}>
            Valor a cobrar agora
          </Typography>
          <Typography sx={{color: "#14b8a6", fontWeight: 900, fontSize: "1.8rem"}}>
            R$ {amountBrl.toFixed(2).replace(".", ",")}
          </Typography>
          <Typography sx={{color: "rgba(226,232,240,0.5)", fontSize: "0.8rem"}}>
            Proporcional ao período restante do ciclo atual. Aceita cartão e boleto.
          </Typography>
        </Box>

        <PaymentElement options={{layout: "accordion"}} />

        {error && (
          <Alert severity="error" sx={{bgcolor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5"}}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onCancel}
            disabled={loading}
            sx={{borderColor: "rgba(148,163,184,0.3)", color: "rgba(226,232,240,0.7)"}}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !stripe}
            sx={{
              background: "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)",
              fontWeight: 800,
            }}
          >
            {loading ? <CircularProgress size={20} sx={{color: "#fff"}} /> : "Confirmar"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// ── Public dialog component ───────────────────────────────────────────────────

export type StripePaymentDialogProps = {
  open: boolean;
  clientSecret: string;
  amountBrl: number;
  expectedModuleIds?: string[];
  title?: string;
  description?: string;
  onSuccess: (intentId?: string) => void | Promise<void>;
  onClose: () => void;
};

export const StripePaymentDialog = ({
  open,
  clientSecret,
  amountBrl,
  expectedModuleIds,
  title = "Pagamento",
  description,
  onSuccess,
  onClose,
}: StripePaymentDialogProps) => {
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    if (!open) setProcessingPayment(false);
  }, [open, clientSecret]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "rgba(15,23,42,0.98)",
          border: "1px solid rgba(20,184,166,0.2)",
          borderRadius: 3,
          boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        },
      }}
    >
      <DialogTitle sx={{pb: 1}}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockIcon sx={{color: "#14b8a6", fontSize: 20}} />
            <Typography fontWeight={800} sx={{color: "#f8fafc"}}>{title}</Typography>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{color: "rgba(226,232,240,0.5)"}}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {description && (
        <Box sx={{px: 3, pb: 1}}>
          <Typography sx={{color: "rgba(226,232,240,0.7)", fontSize: "0.9rem"}}>{description}</Typography>
        </Box>
      )}

      <Divider sx={{borderColor: "rgba(148,163,184,0.1)", mx: 3}} />

      <DialogContent>
        {processingPayment ? (
          <StripePaymentProcessing
            expectedModuleIds={expectedModuleIds}
            onConfirmed={onSuccess}
            continueLabel="Fechar"
            onContinue={onClose}
          />
        ) : !stripePromise ? (
          <Alert severity="warning">
            Chave pública de pagamento não configurada. Contate o suporte.
          </Alert>
        ) : clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{clientSecret, appearance: elementAppearance}}
          >
            <PaymentFormInner
              amountBrl={amountBrl}
              clientSecret={clientSecret}
              onSuccess={(intentId) => {
                if (clientSecret.startsWith("seti_")) {
                  return onSuccess(intentId);
                }
                setProcessingPayment(true);
              }}
              onCancel={onClose}
            />
          </Elements>
        ) : (
          <Alert severity="error">
            O servidor não retornou a autorização do pagamento. Tente novamente ou contate o suporte.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};
